import { inject, Injectable, Signal, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SetRoomsEvent, Level, ApiBuilding, ApiHeatLoadCalculation, CalculationSummary, CalculationBase, Building } from '../../models';
import { CRYPTO } from '../../tokens';
import { LevelNamePipe } from '../../pipes';
import { RoomByRoomApiService } from '../room-by-room-api.service';
import {
    mapApiBuildingToCalculationSummary,
    mapApiBuildingToLevelsView,
    mapHeatLoadCalculationsToApiModel,
    mapLevelsToApiModel,
    mapRoomsToApiModel,
    mapSlabsToApiModel
} from './building.mappers';
import {
    createLowerLevel,
    createUpperLevel,
    deleteLevel,
    deleteRoom,
    getNextLowerLevelNumber,
    getNextUpperLevelNumber,
    setRoomsToLevel,
    toggleHeatingAndCooling,
    updateCalculationSummary
} from './building.domain';
import { BuildingType, HeatLoadCalculationMethod } from '../../../../lib';
import { DefaultValues } from '../../models/default-values.interface';
import { DEFAULT_INCLUDE_TOTAL_BUILDING, DEFAULT_ROOM_HEIGHT, DEFAULT_TOTAL_LIVING_AREA } from '../../consts';

@Injectable({
    providedIn: 'root'
})
export class BuildingService {
    private _buildingSignal = signal<Building | null>(null);
    private _levelsSignal = signal<Level[]>([]);
    private _calculationSummarySignal = signal<CalculationSummary | undefined>(undefined);
    private _calculationBaseSignal = signal<CalculationBase>({ totalLivingArea: 0, includeTotalBuilding: false });
    private _defaultValuesSignal = signal<DefaultValues>({ levelHeight: DEFAULT_ROOM_HEIGHT });

    private _crypto = inject(CRYPTO);
    private _innerLoadingSignal = signal<boolean>(false);
    private _innerErrorSignal = signal<boolean>(false);
    private _loadingSignal = signal<boolean>(false);
    private _errorSignal = signal<boolean>(false);
    private _activeProjectId: string | null = null;
    private _buildingType?: BuildingType;
    private _apiHeatLoadCalculations: ApiHeatLoadCalculation[] = [];
    private _roomByRoomApiService = inject(RoomByRoomApiService);
    private _levelNamePipe = inject(LevelNamePipe);

    public getBuilding(): Signal<Building | null> {
        return this._buildingSignal.asReadonly();
    }

    // TODO: remove levels from state since we store entire building
    public getLevels(): Signal<Level[]> {
        return this._levelsSignal.asReadonly();
    }

    public getInnerLoading(): Signal<boolean> {
        return this._innerLoadingSignal.asReadonly();
    }

    public getInnerError(): Signal<boolean> {
        return this._innerErrorSignal.asReadonly();
    }

    public getCalculationSummary(): Signal<CalculationSummary | undefined> {
        return this._calculationSummarySignal.asReadonly();
    }

    public getCalculationBase(): Signal<CalculationBase> {
        return this._calculationBaseSignal.asReadonly();
    }

    public getDefaultValues(): Signal<DefaultValues> {
        return this._defaultValuesSignal.asReadonly();
    }

    public getLoading(): Signal<boolean> {
        return this._loadingSignal.asReadonly();
    }

    public getError(): Signal<boolean> {
        return this._errorSignal.asReadonly();
    }

    public loadProject(projectId: string): void {
        this._activeProjectId = projectId;
        this._loadingSignal.set(true);
        this._errorSignal.set(false);

        this._roomByRoomApiService.getProject$(projectId).subscribe({
            next: project => {
                this._buildingType = project.buildingType;
                this._buildingSignal.set({ ...project.building });
                this._apiHeatLoadCalculations = [...(project.building?.heatLoadCalculations ?? [])];

                this._levelsSignal.set(mapApiBuildingToLevelsView(project.building));
                this._calculationSummarySignal.set(mapApiBuildingToCalculationSummary(project.building));
                this._calculationBaseSignal.set(this.extractCalculationBase(project.building));
                this._defaultValuesSignal.set(this.extractDefaultValues(project.building));

                this._loadingSignal.set(false);
                this._errorSignal.set(false);
            },
            error: err => {
                console.error('Failed to load project levels', err);
                this._loadingSignal.set(false);
                this._errorSignal.set(true);
                this._levelsSignal.set([]);
                this._calculationSummarySignal.set(undefined);
                this._calculationBaseSignal.set({ totalLivingArea: 0, includeTotalBuilding: false });
                this._defaultValuesSignal.set({
                    levelHeight: DEFAULT_ROOM_HEIGHT,
                    typeOfWindow: undefined,
                    typeOfSunProtection: undefined
                });
            }
        });
    }

    public updateDefaultValues(defaultValues: DefaultValues): void {
        if (!this._activeProjectId) return;

        this._innerLoadingSignal.set(true);

        this._roomByRoomApiService
            .patchProject$(this._activeProjectId, {
                building: {
                    buildingLevelHeight: defaultValues.levelHeight,
                    standardTypeOfWindow: defaultValues.typeOfWindow,
                    standardTypeOfSunProtection: defaultValues.typeOfSunProtection
                } as ApiBuilding
            })
            .subscribe({
                next: () => {
                    const building = this._buildingSignal();

                    if (building) {
                        this._buildingSignal.set({
                            ...building,
                            buildingLevelHeight: defaultValues.levelHeight,
                            standardTypeOfWindow: defaultValues.typeOfWindow,
                            standardTypeOfSunProtection: defaultValues.typeOfSunProtection
                        });
                    }

                    this._defaultValuesSignal.set(defaultValues);
                    this._innerLoadingSignal.set(false);
                },
                error: err => {
                    console.error('Failed to update default values', err);
                    this._innerLoadingSignal.set(false);
                }
            });
    }

    public updateCalculationBase(calculationBase: CalculationBase): void {
        if (!this._activeProjectId) return;

        this.patchBuildingToApi(this._levelsSignal(), { calculationBase });
    }

    public addUpperLevel(): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const nextUpperLevelNumber = getNextUpperLevelNumber(levels);
        const levelName = this._levelNamePipe.transform(nextUpperLevelNumber);
        const updatedLevels = createUpperLevel(levels, this._crypto.randomUUID(), nextUpperLevelNumber, levelName);

        this.patchBuildingToApi(updatedLevels);
    }

    public addLowerLevel(): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const nextLowerLevelNumber = getNextLowerLevelNumber(levels);
        const levelName = this._levelNamePipe.transform(nextLowerLevelNumber);
        const updatedLevels = createLowerLevel(levels, this._crypto.randomUUID(), nextLowerLevelNumber, levelName);

        this.patchBuildingToApi(updatedLevels);
    }

    public async syncLocalizedNamesBeforeFullReload(): Promise<unknown> {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        if (!levels.length) return;

        const updatedLevels = levels.map(level => ({
            ...level,
            name: this._levelNamePipe.transform(level.number)
        }));

        const hasNameChanges = updatedLevels.some((updatedLevel, index) => updatedLevel.name !== levels[index].name);
        if (!hasNameChanges) return;

        return await firstValueFrom(
            this._roomByRoomApiService.patchProject$(this._activeProjectId, {
                building: {
                    levels: mapLevelsToApiModel(updatedLevels)
                }
            })
        );
    }

    public setRoomsToLevel(event: SetRoomsEvent): void {
        if (!this._activeProjectId) return;

        const defaultRoomHeight = this._defaultValuesSignal().levelHeight ?? DEFAULT_ROOM_HEIGHT;

        const levels = this._levelsSignal();
        const updatedLevels = setRoomsToLevel(levels, event, () => this._crypto.randomUUID(), defaultRoomHeight);
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public deleteRoom(roomId: string): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = deleteRoom(levels, roomId);
        if (!updatedLevels) return;
        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public updateBuilding(building: ApiBuilding | null): void {
        if (!this._activeProjectId || !building) return;

        this._innerLoadingSignal.set(true);
        this._innerErrorSignal.set(false);

        const buildingPayload: ApiBuilding = {
            rooms: building.rooms,
            slabs: building.slabs,
            levels: building.levels,
            walls: building.walls,
            windows: building.windows
        };

        const heatLoadCalculations = mapHeatLoadCalculationsToApiModel(this._apiHeatLoadCalculations, this._buildingType);

        if (heatLoadCalculations) {
            buildingPayload.heatLoadCalculations = heatLoadCalculations;
        }

        this._roomByRoomApiService
            .patchProject$(this._activeProjectId, {
                building: buildingPayload
            })
            .subscribe({
                next: () => {
                    this._innerLoadingSignal.set(false);
                    this._innerErrorSignal.set(false);
                    this.loadProject(this._activeProjectId!);
                },
                error: _error => {
                    this._innerLoadingSignal.set(false);
                    this._innerErrorSignal.set(true);
                }
            });
    }

    public deleteLevel(levelId: string): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = deleteLevel(levels, levelId);
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public toggleHeatingAndCooling(roomId: string, { heating, cooling }: { heating?: boolean; cooling?: boolean }): void {
        if (!this._activeProjectId) return;

        const levels = this._levelsSignal();
        const updatedLevels = toggleHeatingAndCooling(levels, roomId, { heating, cooling });
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    private patchBuildingToApi(
        updatedLevels: Level[],
        options?: {
            reload?: boolean;
            addInitialCalculation?: boolean;
            innerLoader?: boolean;
            updateCalculations?: boolean;
            calculationBase?: CalculationBase;
        }
    ): void {
        if (!this._activeProjectId) return;

        this._errorSignal.set(false);

        if (options?.innerLoader) {
            this._innerLoadingSignal.set(true);
            this._innerErrorSignal.set(false);
        } else {
            this._loadingSignal.set(true);
        }

        let updatedCalculations = this._calculationSummarySignal();

        if (options?.updateCalculations) {
            updatedCalculations = updateCalculationSummary(
                this._calculationSummarySignal()?.totalLivingArea ?? DEFAULT_TOTAL_LIVING_AREA,
                this._calculationSummarySignal()?.includeTotalBuilding ?? DEFAULT_INCLUDE_TOTAL_BUILDING,
                updatedLevels
            );
        } else if (options?.calculationBase) {
            updatedCalculations = updateCalculationSummary(
                options.calculationBase.totalLivingArea,
                options.calculationBase.includeTotalBuilding,
                updatedLevels
            );
        }

        const buildingPayload: ApiBuilding = {
            levels: mapLevelsToApiModel(updatedLevels),
            rooms: mapRoomsToApiModel(updatedLevels, this._buildingSignal()?.rooms || {}, DEFAULT_ROOM_HEIGHT),
            slabs: mapSlabsToApiModel(updatedLevels, this._buildingSignal()?.slabs || {})
        };

        if (options?.calculationBase) {
            buildingPayload.livingArea = options.calculationBase.totalLivingArea;
            const updatedRoomByRoomCalculation = this.updateLeftoverAreaActivation(options.calculationBase.includeTotalBuilding);

            if (updatedRoomByRoomCalculation) {
                buildingPayload.heatLoadCalculations = mapHeatLoadCalculationsToApiModel(
                    [updatedRoomByRoomCalculation],
                    this._buildingType
                );
            }
        } else {
            const heatLoadCalculations = mapHeatLoadCalculationsToApiModel(this._apiHeatLoadCalculations, this._buildingType);

            if (heatLoadCalculations && options?.addInitialCalculation) {
                buildingPayload.heatLoadCalculations = heatLoadCalculations;
            }
        }

        this._roomByRoomApiService
            .patchProject$(this._activeProjectId, {
                building: buildingPayload
            })
            .subscribe({
                next: () => {
                    if (options?.reload) {
                        this._innerLoadingSignal.set(false);
                        this.loadProject(this._activeProjectId!);
                        return;
                    }

                    const building = this._buildingSignal();
                    if (building) {
                        this._buildingSignal.set({
                            ...building,
                            levels: buildingPayload.levels,
                            rooms: buildingPayload.rooms,
                            slabs: buildingPayload.slabs
                        });
                    }

                    this._levelsSignal.set([...updatedLevels]);
                    this._calculationSummarySignal.set(updatedCalculations);
                    if (buildingPayload.heatLoadCalculations) {
                        this._apiHeatLoadCalculations = [...buildingPayload.heatLoadCalculations];
                    }

                    if (options?.calculationBase) {
                        this._calculationBaseSignal.set(options.calculationBase);
                    }

                    this._loadingSignal.set(false);
                    this._innerLoadingSignal.set(false);
                    this._innerErrorSignal.set(false);
                    this._errorSignal.set(false);
                },
                error: _error => {
                    this._loadingSignal.set(false);
                    this._innerLoadingSignal.set(false);
                    if (!options?.innerLoader) {
                        this._errorSignal.set(true);
                        this._levelsSignal.set([]);
                        this._calculationSummarySignal.set(undefined);
                    } else {
                        this._innerErrorSignal.set(true);
                    }
                }
            });
    }

    private extractCalculationBase(building?: ApiBuilding): CalculationBase {
        const roomByRoomCalculation = this.getRoomBasedCalculation(building?.heatLoadCalculations);

        const includeTotalBuilding = Boolean(roomByRoomCalculation?.roomCalculations?.['leftover-area']?.isActive);

        return {
            totalLivingArea: building?.livingArea ?? 0,
            includeTotalBuilding
        };
    }

    private extractDefaultValues(building?: ApiBuilding): DefaultValues {
        return {
            levelHeight: building?.buildingLevelHeight ?? DEFAULT_ROOM_HEIGHT,
            typeOfWindow: building?.standardTypeOfWindow,
            typeOfSunProtection: building?.standardTypeOfSunProtection
        };
    }

    private getRoomBasedCalculation(calculations: ApiHeatLoadCalculation[] | undefined): ApiHeatLoadCalculation | undefined {
        return calculations?.find(calculation => calculation.method === HeatLoadCalculationMethod.RoomByRoom);
    }

    private updateLeftoverAreaActivation(includeTotalBuilding: boolean): ApiHeatLoadCalculation | null {
        const roomByRoomCalculation = this.getRoomBasedCalculation(this._apiHeatLoadCalculations);

        if (!roomByRoomCalculation) {
            return null;
        }

        const leftoverAreaCalculation = roomByRoomCalculation.roomCalculations?.['leftover-area'] ?? {
            roomId: 'leftover-area',
            baseHeatLoad: 0,
            baseCoolingLoad: 0,
            baseHeatDemand: 0,
            isActive: false
        };

        roomByRoomCalculation.roomCalculations = {
            ...(roomByRoomCalculation.roomCalculations ?? {}),
            'leftover-area': {
                ...leftoverAreaCalculation,
                isActive: includeTotalBuilding
            }
        };
        return roomByRoomCalculation;
    }
}
